import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ANDROID_APP_URL,
  IOS_APP_URL,
  getAppRedirectUrl,
} from '../config/appStores';
import './DownloadApp.css';

function qrImageUrl(data, size = 280) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&color=1a1208&bgcolor=ffffff&data=${encodeURIComponent(data)}`;
}

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: 0.12 + i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

/**
 * Public app download page — QR opens https://extranet.tabtrips.com/get-app
 */
const DownloadApp = () => {
  const redirectUrl = getAppRedirectUrl();
  const qrSrc = qrImageUrl(redirectUrl, 280);

  useEffect(() => {
    const id = 'download-app-fonts';
    if (document.getElementById(id)) return undefined;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Fraunces:opsz,wght@9..144,600;9..144,700&display=swap';
    document.head.appendChild(link);
    return undefined;
  }, []);

  return (
    <div className="da-page">
      <div className="da-glow da-glow--a" aria-hidden />
      <div className="da-glow da-glow--b" aria-hidden />
      <div className="da-grid" aria-hidden />

      <header className="da-nav">
        <a className="da-brand" href="https://extranet.tabtrips.com/login">
          <img src="/favicon.ico" alt="" width={28} height={28} />
          <span>TabTrips Extranet</span>
        </a>
        <RouterLink className="da-nav-link" to="/login">
          Partner login
        </RouterLink>
      </header>

      <main className="da-hero">
        <div className="da-hero-copy">
          <motion.p
            className="da-kicker"
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            Partner mobile app
          </motion.p>

          <motion.h1
            className="da-title"
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            TabTrips
            <span>Extranet</span>
          </motion.h1>

          <motion.p
            className="da-lede"
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            Rates, inventory, and bookings — updated from your phone, live on TabTrips.
          </motion.p>

          <motion.div
            className="da-ctas"
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="show"
          >
            <a
              className="da-btn da-btn--primary"
              href={ANDROID_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-google-play" aria-hidden />
              Google Play
            </a>
            <a
              className="da-btn da-btn--ghost"
              href={IOS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fab fa-apple" aria-hidden />
              App Store
            </a>
          </motion.div>
        </div>

        <motion.div
          className="da-qr"
          custom={4}
          variants={fadeUp}
          initial="hidden"
          animate="show"
        >
          <div className="da-qr-ring" aria-hidden />
          <div className="da-qr-panel">
            <img
              src={qrSrc}
              alt="QR code — scan to open TabTrips Extranet app download"
              width={280}
              height={280}
            />
            <p>Scan to download our app</p>
          </div>
        </motion.div>
      </main>

      <p className="da-foot">For registered TabTrips property partners.</p>
    </div>
  );
};

export default DownloadApp;
